import { useState } from 'react';

import type { ContactDossier } from './types';

import { Button, Label, PasswordInput } from '../Input2';
import { General, Arrows } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import { cn } from '../cn';

interface Props {
  /** The persisted dossier, or null if none exists yet. */
  dossier: ContactDossier | null;
  /**
   * True iff the candidate-research worker is actively building (or about
   * to retry building) a dossier for this contact. Drives the spinner /
   * "Generating dossier…" header state when `dossier` is null.
   */
  isGenerating: boolean;
  /**
   * Whether the team currently has a Tavily API key configured. The dossier
   * pipeline soft-fails without one (no dossier ever lands), so the empty /
   * generating states surface an inline "Set Tavily API key" button when
   * this is false.
   */
  hasTavilyKey: boolean;
  /**
   * Callback fired when the inline popup saves a new Tavily key. The pure
   * component doesn't know about tRPC — the wrapper threads this through
   * to the `updateUserTeamData` mutation. Returns a promise so the popup
   * can disable the Save button while in flight.
   */
  onSaveTavilyKey: (key: string) => Promise<unknown>;
  /**
   * Fires when the user clicks the manual Generate/Regenerate button. Force-
   * refreshes the dossier (bypasses the 6-month freshness short-circuit).
   */
  onRegenerateDossier: () => Promise<unknown>;
  /** True while the manual regenerate mutation is in flight. */
  isRegeneratingDossier: boolean;
}

/**
 * Renders the per-contact research dossier on the contact detail page. The
 * dossier is generated lazily by the candidate-research worker when the
 * contact is added to a campaign (see `apiWorker/contactDossier.service.ts`).
 *
 * Three render modes:
 *   - `dossier != null` → six stacked subsections + sources list.
 *   - `dossier == null && isGenerating` → spinner + "Generating dossier…".
 *   - `dossier == null && !isGenerating` → muted "will be generated when
 *     added to a campaign" hint so the empty section doesn't feel broken.
 *
 * Pure component — no hooks-into-data, no platform APIs. Lives in
 * @poseidon/components so the same section can render in Electron /
 * extension surfaces once they grow the contact detail view.
 */
export function ContactDossierSection({
  dossier,
  isGenerating,
  hasTavilyKey,
  onSaveTavilyKey,
  onRegenerateDossier,
  isRegeneratingDossier,
}: Props) {
  if (dossier == null) {
    return (
      <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          <span>Dossier</span>
          {isGenerating && <WaveSpinner />}
        </div>
        {isGenerating && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Generating dossier — usually 3–6 seconds.
          </div>
        )}
        {!isGenerating && (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Dossier will be generated the next time this contact is added to a campaign.
          </div>
        )}
        {!isGenerating && hasTavilyKey && (
          <div className="mt-2">
            <Button
              size="xs"
              color="blue" tone="outline"
              disabled={isRegeneratingDossier}
              icon={isRegeneratingDossier ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
              onClick={() => {
                void onRegenerateDossier();
              }}>
              Generate dossier
            </Button>
          </div>
        )}
        {!hasTavilyKey && (
          <div className="mt-2">
            <TavilyKeySetter onSave={onSaveTavilyKey} />
          </div>
        )}
      </div>
    );
  }

  const updatedRelative = formatRelativeFromIso(dossier.updatedAt);

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Dossier</span>
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-xs',
            dossier.isStale ? 'text-yellow-700 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400',
          )}>
            Updated {updatedRelative}
            {dossier.isStale && ' · Stale — will refresh on next campaign add'}
          </span>
          <Button
            size="xs"
            color="blue" tone="outline"
            disabled={isRegeneratingDossier}
            icon={isRegeneratingDossier ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
            tooltip="Force-refresh the dossier now"
            onClick={() => {
              void onRegenerateDossier();
            }}>
            Regenerate
          </Button>
        </div>
      </div>

      {dossier.background.length > 0 && (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
          {dossier.background}
        </p>
      )}

      <DossierList title="Career highlights" items={dossier.careerHighlights} />
      <DossierList title="Recent signals" items={dossier.recentSignals} />
      <DossierList title="Wealth signals" items={dossier.wealthSignals} />
      <DossierList title="Talking points" items={dossier.talkingPoints} />

      {dossier.recentItems.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Sources
          </div>
          <div className="mt-1 flex min-w-0 flex-col gap-2">
            {dossier.recentItems.map((item, idx) => (
              <div key={`${item.url}-${idx}`} className="flex min-w-0 flex-col">
                {/* Plain anchor with the same slate/text color the previous
                    ButtonA used. Truncate (not wrap) so long titles stay on
                    one line with an ellipsis — `min-w-0` is required on the
                    ancestor flex chain or the truncate would be a no-op. */}
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={item.title}
                  className="flex min-w-0 items-center gap-1.5 text-sm text-slate-900 hover:underline dark:text-slate-100">
                  <General.LinkExternal01 className="size-3 shrink-0" />
                  <span className="min-w-0 truncate">{item.title}</span>
                </a>
                <span className="ml-[18px] text-xs text-gray-500 dark:text-gray-400">
                  {item.snippet}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ListProps {
  title: string;
  items: string[];
}

/**
 * Renders a labeled bullet list. Only collapses when 3+ items would be
 * hidden — hiding just 1 or 2 isn't worth the extra "Show N more" click
 * and reads as if there's more interesting content behind the chevron
 * than there actually is.
 */
function DossierList({ title, items }: ListProps) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;

  const SHORT_LIMIT = 3;
  const MIN_HIDDEN_FOR_COLLAPSE = 3;
  const showToggle = items.length - SHORT_LIMIT >= MIN_HIDDEN_FOR_COLLAPSE;
  const visible = expanded || !showToggle ? items : items.slice(0, SHORT_LIMIT);
  const hiddenCount = items.length - SHORT_LIMIT;

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </div>
      <ul className="mt-1 flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
        {visible.map((line, idx) => (
          <li key={idx} className="flex gap-2">
            <span aria-hidden className="select-none text-gray-400 dark:text-gray-500">•</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {showToggle && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
          <Arrows.ChevronDown
            className={cn('size-3 transition-transform', expanded && 'rotate-180')}
          />
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

/**
 * Inline "Set Tavily API key" popover. Renders a small blue button; clicking
 * it expands a one-line password input + Save. Closes on successful save.
 *
 * Lives in the dossier section (not the Automation settings page) because
 * the failure surface is "you opened a contact and the dossier didn't run"
 * — the user shouldn't have to navigate to Settings to unblock themselves.
 */
function TavilyKeySetter({ onSave }: { onSave: (key: string) => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <Button size="xs" color="blue" tone="soft" onClick={() => setOpen(true)}>
        Set Tavily API key
      </Button>
    );
  }

  const handleSave = async () => {
    if (value.trim().length === 0) return;
    setSaving(true);
    try {
      await onSave(value.trim());
      setOpen(false);
      setValue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900">
      <Label size="sm" htmlFor="dossier-tavily-key">
        Tavily API key
      </Label>
      <PasswordInput
        id="dossier-tavily-key"
        name="tavilyApiKey"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="tvly-…"
        disabled={saving}
      />
      <div className="flex justify-end gap-2">
        <Button
          size="xs"
          text
          onClick={() => {
            setOpen(false);
            setValue('');
          }}
          disabled={saving}>
          Cancel
        </Button>
        <Button size="xs" color="blue" onClick={handleSave} disabled={saving || value.trim().length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <span className="text-2xs text-gray-500 dark:text-gray-400">
        Stored per (you, this team) — won&apos;t affect teammates.
      </span>
    </div>
  );
}

/**
 * Human-readable "3 months ago" / "yesterday" string from an ISO timestamp.
 * Kept simple in-file rather than pulled from luxon because the component is
 * imported into Electron/extension surfaces too and we don't want the date
 * lib coupling.
 */
function formatRelativeFromIso(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo} month${diffMo === 1 ? '' : 's'} ago`;
  const diffYr = Math.round(diffMo / 12);
  return `${diffYr} year${diffYr === 1 ? '' : 's'} ago`;
}
